import { axiosInstance } from "../../../config/axios";
import endpoints from "../../../config/endpoints";


export interface GenerateEduConetentPayload {
  content_topic: string;
  content_type: string;
  category: string;
  language: string;
  additional_instructions: string;
}

export interface AssignContentPayload {
  contentId: string;
  targetType: string;
  targetIds: string[];
}

export interface EducationContentResponse {
  id: number;
  content_topic: string;
  content_type: string;
  category: string;
  language: string;
  additional_instructions: string;
  content_url: string;
  content_title: string;
  content_description: string;
  video_channel: string;
  video_thumbnail_url: string;
  ai_ranking_score: number;
  ai_ranking_reason: string;
  article_summary: string | null;
  word_count: number | null;
  generated_by_admin_id: number;
  created_at: string;
  updated_at: string;
}

export interface AssignContent {
  content_id: string;
  audience_type: "all_patients" | "custom_selection" | "by_language" | "by_diagnosis";
  user_ids?: string[];
  language_filter?: string;
  diagnosis_filter?: string;
  schedule_type?: "send_now" | "custom_schedule";
  schedule_datetime?: string;
}



export const generateEducationContent = async (payload: GenerateEduConetentPayload): Promise<EducationContentResponse> => {
  const response = await axiosInstance.post(`${import.meta.env.PROD 
    ? endpoints().education.generateEducationContent 
    : `/api/v1${endpoints().education.generateEducationContent}`}`, payload
  );
  return response.data;
};

export interface PaginatedEducationContents {
  data: EducationContentResponse[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface PatientAssignment {
  id: number;
  user_id: number;
  diagnosis: string;
  treatment_plan: string;
  return_date: string;
  content_type: string;
  content_url: string | null;
  content_title: string;
  content_description: string | null;
  video_channel: string | null;
  video_thumbnail_url: string | null;
  ai_ranking_score: number | null;
  ai_ranking_reason: string | null;
  article_summary: string | null;
  word_count: number | null;
  status: 'sent' | 'failed' | 'pending';
  scheduled_send_date: string;
  scheduled_send_time: string;
  sent_at: string | null;
  failure_reason: string | null;
  assigned_by_admin_id: number;
  created_at: string;
  updated_at: string;
}

export interface AssignmentPaginationData {
  total_items: number;
  page: number;
  limit: number;
  total_pages: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

export interface PaginatedPatientAssignments {
  data: PatientAssignment[];
  pagination: AssignmentPaginationData;
}

export interface EducationMetrics {
  library: {
    total_content: number,
    by_type: {
      video_script: number,
      article: number
    }
  },
  assignments: {
    total: number,
    pending: number,
    sent: number,
    failed: number,
    delivery_rate_percent: number,
    by_content_type: {
      video_script: number,
      article: number
    },
    unique_patients_reached: number
  }
}


export const getEducationContents = async (params: {
    page: number;
    limit: number;
    search?: string;
    content_type?: string;
    category?: string;
}): Promise<PaginatedEducationContents> => {
  const response = await axiosInstance.get(`${import.meta.env.PROD 
    ? endpoints().education.getEducationContents 
    : `/api/v1${endpoints().education.getEducationContents}`}`, {
    params
  });
  return response.data;
}

export const assignContentToPatients = async (payload: AssignContent): Promise<EducationContentResponse> => {
  const response = await axiosInstance.post(`${import.meta.env.PROD 
    ? endpoints().education.assignContentToPatients 
    : `/api/v1${endpoints().education.assignContentToPatients}`}`, payload
  );
  return response.data;
};

export const getPatientAssignments = async (params: {
  page: number;
  limit: number;
  patient_id?: number;
  status?: string;
  search?: string;
}): Promise<PaginatedPatientAssignments> => {
  const response = await axiosInstance.get(`${import.meta.env.PROD
    ? endpoints().education.getPatientAssignments
    : `/api/v1${endpoints().education.getPatientAssignments}`}`, {
    params
  });
  return response.data;
};

export const educationMetrics = async (): Promise<EducationMetrics> => {
  const response = await axiosInstance.get(`${import.meta.env.PROD
    ? endpoints().education.educationMetrics
    : `/api/v1${endpoints().education.educationMetrics}`}`);
  return response.data;
}
